// API types and the one fetch helper. The shapes here mirror the backend
// routes byte-for-byte (packages/backend/src/routes/{test,admin}.ts) — if a
// field changes there, change it here.

import type { BigFiveFacetScores, BigFiveTrait } from "@careers/shared";

export type Lang = "en" | "ja";

/** One questionnaire item as the API sends it: position + bilingual text. */
export interface PublicQuestion {
  order: number;
  en: string;
  ja: string;
}

/** GET /api/test/:token (200). 404 means unknown or voided. */
export type TestStatusResponse =
  | { status: "pending"; questions: PublicQuestion[] }
  | { status: "completed" };

/** What submit returns and what the admin detail's `results` holds. */
export interface TestResults {
  percentiles: Record<BigFiveTrait, number>;
  summary: string;
  facets: BigFiveFacetScores;
}

/** The submit payload — must match the backend's JSON schema exactly
 *  (additionalProperties: false at the top level). */
export interface SubmitBody {
  answers: { order: number; value: number }[];
  lang: Lang;
  timings: { activeSeconds: number; perPage: number[] };
}

export interface EventCounts {
  copy: number;
  hidden: number;
  blur: number;
}

/** One row of GET /api/admin/invitations (dates arrive as ISO strings). */
export interface AdminInvitation {
  id: string;
  email: string;
  status: "pending" | "completed" | "void";
  invitedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  lang: string | null;
  /** Client-reported active seconds — a claim, possibly doctored. */
  activeSeconds: number | null;
  /** Server-verified completed − started wall time; null until both exist. */
  wallSeconds: number | null;
  /** Why the timing claims look doctored (server cross-check); [] = clean. */
  timingSuspect: string[];
  eventCounts: EventCounts;
  percentiles: Record<BigFiveTrait, number> | null;
  /** The one-time link — present only while it still works (pending). */
  url: string | null;
}

export interface AdminEvent {
  type: string;
  occurredAt: string;
  meta: unknown;
}

/** GET /api/admin/invitations/:id — the list row plus the full stored data. */
export interface AdminInvitationDetail extends AdminInvitation {
  results: TestResults | null;
  timings: {
    activeSeconds?: number;
    perPage?: number[];
    /** Written by the backend at submit — see crossCheckTimings there. */
    server?: { wallSeconds: number | null; suspect: string[] };
  } | null;
  events: AdminEvent[];
  shareLinks: ShareLink[];
}

/** An active (unrevoked, unexpired) read-only results link. */
export interface ShareLink {
  id: string;
  url: string;
  createdAt: string;
  expiresAt: string;
}

/** GET /api/share/:token (200). 404 means unknown, expired, or revoked. */
export interface SharedResultsResponse {
  email: string;
  completedAt: string;
  expiresAt: string;
  results: TestResults;
}

/** Non-2xx responses surface as this, so callers can branch on `status`
 *  (401 → show login, 409 → already completed, ...). */
export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API error ${status}`);
  }
}

/** fetch + JSON in both directions. Same-origin (the Vite dev proxy / nginx
 *  in prod), so admin cookies flow automatically. 204s resolve to null. */
export async function api<T>(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<T> {
  const res = await fetch(path, {
    method: init?.method ?? "GET",
    headers: init?.body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // Non-JSON error body — status alone is enough for every caller.
    }
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return null as T;
  return (await res.json()) as T;
}

/** mm:ss from a seconds count (63 → "01:03"; hours just widen the minutes). */
export function formatMmSs(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}
